<script setup lang="ts">
import { computed, ref } from "vue";
import { api, ServerError } from "@/api/server";
import { useToasts } from "@/composables/useToasts";
import type { SchoolModeCommand, ScreenTimeEntry } from "@/types/protocol";
import { formatDuration } from "@/utils/format";

const props = defineProps<{ entry: ScreenTimeEntry }>();
const toasts = useToasts();
const busy = ref(false);

const school = computed(() => props.entry.schoolMode);

/** „aktiv bis 16:00" — die Uhrzeit reicht, das Datum ist immer heute. */
const until = computed(() => {
  const value = school.value?.until;
  if (!value) return null;
  return new Date(value).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
});

/**
 * Der Modus gilt dem Benutzer, durchgesetzt wird er je PC. Meldet keiner der aktiven PCs
 * ihn als durchgesetzt, ist die Anzeige eine Absicht und keine Tatsache — das muss man
 * sehen, statt es zu beschoenigen.
 */
const enforced = computed(() => (school.value?.machines.length ?? 0) > 0);

const modeText: Record<SchoolModeCommand, string> = {
  on: "einschalten",
  off: "aussetzen",
  auto: "auf den Wochenplan zuruecksetzen",
};

async function setMode(mode: SchoolModeCommand): Promise<void> {
  if (mode === "on" && !window.confirm(`Schulmodus für „${props.entry.user}" wirklich bis Mitternacht einschalten?`)) return;

  busy.value = true;
  try {
    const result = await api.schoolMode(props.entry.user, mode);
    toasts.info(`Schulmodus ${modeText[mode]}: jetzt ${result.active ? "an" : "aus"} (${result.source}).`);
  } catch (error) {
    toasts.error(error instanceof ServerError ? error.message : String(error));
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <article class="panel card">
    <div class="who">
      <span class="pulse" :class="{ off: !entry.activeNow }" />
      {{ entry.user }}
    </div>
    <div class="big">{{ formatDuration(entry.seconds) }}</div>
    <div class="where">
      <template v-if="entry.activeNow">läuft gerade · {{ entry.machines.join(", ") }}</template>
      <template v-else>pausiert (gesperrt oder abgemeldet)</template>
    </div>

    <div v-if="school" class="school">
      <span class="chip" :class="{ on: school.active, warn: school.active && !enforced }">
        <template v-if="school.active">
          Schulmodus<template v-if="until"> · bis {{ until }}</template> · {{ school.source }}
        </template>
        <template v-else>Schulmodus aus</template>
      </span>
      <span v-if="school.active && !enforced" class="hint" title="Kein PC meldet die Richtlinie als durchgesetzt">
        nicht durchgesetzt
      </span>

      <div class="actions">
        <button type="button" :disabled="busy" @click="setMode('on')">An</button>
        <button type="button" :disabled="busy" @click="setMode('off')">Aus</button>
        <button type="button" :disabled="busy" @click="setMode('auto')">Automatik</button>
      </div>
    </div>
  </article>
</template>

<style scoped>
.card {
  padding: 12px 14px;
}

.who {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.big {
  font-size: 26px;
  font-weight: 600;
  margin: 4px 0 2px;
  font-variant-numeric: tabular-nums;
}

.where {
  color: var(--muted);
  font-size: 12.5px;
}

.school {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--line);
}

.chip {
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--line);
  color: var(--muted);
  font-size: 12px;
}

.chip.on {
  border-color: var(--accent);
  color: var(--accent);
}

.chip.on.warn {
  border-color: var(--warn);
  color: var(--warn);
}

.hint {
  color: var(--warn);
  font-size: 12px;
}

.actions {
  display: flex;
  gap: 6px;
  margin-left: auto;
}

.actions button {
  font-size: 12px;
  padding: 3px 9px;
}

.pulse {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--ok);
  flex: none;
  animation: blink 2s ease-in-out infinite;
}

.pulse.off {
  background: var(--muted);
  animation: none;
}

@keyframes blink {
  50% {
    opacity: 0.35;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pulse {
    animation: none;
  }
}
</style>
